import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS } from '@constants';
import {
    CUSTOMERS_STATUS,
    ORDERS_STATUS,
    PRODUCT_CATALOG_STATUS,
    PURCHASE_EVENT_STATUS,
    SCRIPT_TAG_STATUS
} from '@enums';
import { usePolling, useStore } from '@hooks';
import { Button, Divider, Icon, Typography, WhiteCover } from '@ui';
import { formatString } from '@utils';
import { observer } from 'mobx-react';
import CoverTitle from '../../../../../../../../components/IntegrationKlickly/components/CoverTitle';
import { MODE, SETUP_KEYS } from '../../constants';
import SubdomainCollapse from '../../modals/SubdomainCollapse';
import LineItem from '../../shared/LineItem';
import { StyledCollapseContent, StyledConfirm, StyledError, StyledLink } from './styled';

const StoreSetupManual = observer(
    ({
        title,
        ordersTitle,
        customersTitle,
        showErrorWhenReused,
        variant = MODE.ACCOUNT,
        setModal,
        modal,
        onModalClose
    }) => {
        const [analyticsScriptOpen, setAnalyticsScriptOpen] = useState(false);
        const [purchaseEventOpen, setPurchaseEventOpen] = useState(false);
        const navigate = useNavigate();
        const store = useStore();
        const {
            setupInfo,
            manualStoreSetupFinished,
            getSetupInfo,
            historicalSetupOrdersPending,
            historicalSetupCustomersPending,
            currentAccount
        } = store.accountsStore;
        const { getCampaigns } = store.campaignsStore;

        const { startPolling, stopPolling } = usePolling(getSetupInfo, 10000, false, 2000);

        useEffect(() => {
            getCampaigns();
        }, []);

        useEffect(() => {
            if (
                setupInfo.purchaseEventStatus === PURCHASE_EVENT_STATUS.PENDING ||
                setupInfo.scriptTag.status === SCRIPT_TAG_STATUS.PENDING ||
                historicalSetupOrdersPending ||
                historicalSetupCustomersPending
            ) {
                startPolling();
            } else {
                stopPolling();
            }
        }, [
            setupInfo.purchaseEventStatus,
            setupInfo.scriptTag.status,
            historicalSetupOrdersPending,
            historicalSetupCustomersPending
        ]);

        const getIsOpen = (key) => {
            if (key === SETUP_KEYS.ANALYTICS_SCRIPT) {
                return analyticsScriptOpen;
            }
            if (key === SETUP_KEYS.PURCHASE_EVENT) {
                return purchaseEventOpen;
            }
            return false;
        };

        const getToggleCollapse = (key) => {
            if (key === SETUP_KEYS.ANALYTICS_SCRIPT) {
                return () => setAnalyticsScriptOpen(!analyticsScriptOpen);
            }
            if (key === SETUP_KEYS.PURCHASE_EVENT) {
                return () => setPurchaseEventOpen(!purchaseEventOpen);
            }
            return () => {};
        };

        const getProductCatalogSubtitle = () => {
            if (variant !== MODE.ACCOUNT) return undefined;

            const productSetupSettings = currentAccount?.productSetupSettings;
            if (!productSetupSettings) return undefined;

            const connectionMethods = [
                { key: 'googleFeed', label: 'Google product Feed' },
                { key: 'metaFeed', label: 'Meta Feed' },
                { key: 'csv', label: 'CSV' },
                { key: 'api', label: 'API' },
                { key: 'webhooks', label: 'Webhooks' }
            ];

            const connectedMethod = connectionMethods.find((method) => productSetupSettings[method.key]);

            return connectedMethod ? `(connected via ${connectedMethod.label})` : undefined;
        };

        const setupConfig = {
            'api-setup': {
                title: 'API setup',
                viewMode: true
            },
            [SETUP_KEYS.ANALYTICS_SCRIPT]: {
                title: 'Analytics script setup',
                status: setupInfo.scriptTag.status
            },
            [SETUP_KEYS.PURCHASE_EVENT]: {
                title: 'Purchase events setup',
                status: setupInfo.purchaseEventStatus
            },
            'product-catalog': {
                title: 'Product catalog setup',
                status:
                    setupInfo.productCatalog > 0
                        ? PRODUCT_CATALOG_STATUS.CONNECTED
                        : PRODUCT_CATALOG_STATUS.NOT_CONNECTED,
                subtitle: getProductCatalogSubtitle()
            },
            orders: {
                title: ordersTitle || 'Orders setup',
                status: historicalSetupOrdersPending
                    ? ORDERS_STATUS.PENDING
                    : setupInfo.historical.hasOrders
                    ? ORDERS_STATUS.CONNECTED
                    : ORDERS_STATUS.NOT_CONNECTED,
                subtitle: variant === MODE.ACCOUNT ? '(optional)' : undefined
            },
            customers: {
                title: customersTitle || 'Customers setup',
                status: historicalSetupCustomersPending
                    ? CUSTOMERS_STATUS.PENDING
                    : setupInfo.historical.hasCustomers
                    ? CUSTOMERS_STATUS.CONNECTED
                    : CUSTOMERS_STATUS.NOT_CONNECTED,
                subtitle: variant === MODE.ACCOUNT ? '(optional)' : undefined
            }
        };

        return (
            <WhiteCover
                customTitle={
                    <CoverTitle title={title || 'Connect your store to Klickly'} icon={<Icon.KlicklyLogo />} />
                }
                style={showErrorWhenReused ? {} : BOTTOM_INDENTS.M}
                rightContent={
                    <Button type='link' padding='0' onClick={() => window.open('/docs/api', '_blank')}>
                        API integration guide
                    </Button>
                }
            >
                <Typography.Text type='body2' color={COLORS.$gray60Black} style={BOTTOM_INDENTS.M}>
                    Connecting your store allows you to synchronize your shopping cart data with Klickly&apos;s
                    platform, which enables updates you make to products, prices, and inventory to flow through
                    automatically to Klickly, and powers in-ad buying and accurate commission charging. Please
                    coordinate with your technical team to complete this step, as it involves technical knowledge for
                    proper execution. If needed, refer to our detailed{' '}
                    <StyledLink onClick={() => window.open('/docs/api', '_blank')}>API integration guide</StyledLink>{' '}
                    for assistance.
                </Typography.Text>

                {Object.entries(setupConfig).map(([key, data], index, array) => {
                    const isLast = index === array.length - 1;
                    const isAnalyticsScript = key === SETUP_KEYS.ANALYTICS_SCRIPT;
                    const isPurchaseEvent = key === SETUP_KEYS.PURCHASE_EVENT;
                    const isCollapsible = variant === MODE.ACCOUNT && (isAnalyticsScript || isPurchaseEvent);
                    const isOpen = getIsOpen(key);
                    const toggleCollapse = getToggleCollapse(key);

                    return (
                        <div
                            key={key}
                            style={showErrorWhenReused && isLast ? BOTTOM_INDENTS.NONE : BOTTOM_INDENTS.S}
                            data-e2e={`integrations_${formatString(data.title)}_block`}
                        >
                            <LineItem
                                setModal={setModal}
                                modal={modal}
                                setupKey={key}
                                key={key}
                                data={data}
                                isManual
                                bottomMargin={variant === MODE.ACCOUNT ? { marginBottom: 0 } : undefined}
                                isCollapsible={isCollapsible}
                                isOpen={isOpen}
                                onToggle={toggleCollapse}
                                variant={variant}
                                removeBottomMargin={variant === MODE.CAMPAIGN && showErrorWhenReused && isLast}
                                style={
                                    variant === MODE.ACCOUNT && isCollapsible && isOpen
                                        ? {
                                              borderBottomLeftRadius: 0,
                                              borderBottomRightRadius: 0
                                          }
                                        : {}
                                }
                                onModalClose={onModalClose}
                            />
                            {variant === MODE.ACCOUNT && isAnalyticsScript && analyticsScriptOpen && (
                                <StyledCollapseContent>
                                    <Divider margin='0' />
                                    <SubdomainCollapse
                                        openedCollapse={SETUP_KEYS.ANALYTICS_SCRIPT}
                                        initialDomains={setupInfo.restCustomDomains || []}
                                    />
                                </StyledCollapseContent>
                            )}
                            {variant === MODE.ACCOUNT && isPurchaseEvent && purchaseEventOpen && (
                                <StyledCollapseContent>
                                    <Divider margin='0' />
                                    <SubdomainCollapse
                                        openedCollapse={SETUP_KEYS.PURCHASE_EVENT}
                                        initialDomains={setupInfo.restCustomDomains || []}
                                    />
                                </StyledCollapseContent>
                            )}
                        </div>
                    );
                })}

                {showErrorWhenReused ? null : manualStoreSetupFinished ? (
                    <StyledConfirm align='center' justify='space-between'>
                        <Typography.Text
                            type='descriptor1'
                            color={COLORS.$robinBlue}
                            data-e2e='integrations_connected-to-klickly_text'
                        >
                            Your store has been connected to Klickly!
                        </Typography.Text>
                        <Button
                            onClick={() => navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_1)}
                            type='primary'
                            size='large'
                            disabled={
                                store.campaignsStore.hasPreLaunchedCampaign ||
                                store.accountsStore.isManualWithSyncInProgressOrFailed
                            }
                            iconRight={<Icon size='24px' color={COLORS.$white} component={() => <Icon.PlusIcon />} />}
                        >
                            Add New Campaign
                        </Button>
                    </StyledConfirm>
                ) : (
                    <StyledError>
                        <Typography.Text type='descriptor1' color={COLORS.$tartRed}>
                            Please complete all required setup steps
                        </Typography.Text>
                    </StyledError>
                )}
            </WhiteCover>
        );
    }
);

export default StoreSetupManual;
